/**
 * DocumentScopesEditor — Iter27.
 *
 * Per-user document scope access editor. Renders a small grid of 3 scopes
 * (Schools / Women Development / General) × 2 actions (View, Upload).
 *
 * - Fetches scope catalog from /api/admin/permissions/document-scopes/catalog
 * - For super_admin role, shows a locked notice (always full access)
 * - Auto-fills from /api/admin/permissions/preset/{role} when the parent role
 *   changes (same pattern as PermissionMatrixEditor)
 * - Emits the document_scopes object via onChange so the parent form can submit
 *   it alongside permissions_v2.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Alert, AlertDescription } from './ui/alert';
import { Lock, FolderOpen, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCOPE_TONE = {
  schools: 'border-blue-200 bg-blue-50/60 dark:bg-blue-900/15',
  women: 'border-pink-200 bg-pink-50/60 dark:bg-pink-900/15',
  general: 'border-slate-200 bg-slate-50/60 dark:bg-slate-800/30',
};

const DocumentScopesEditor = ({ role, value, onChange, autoFillOnRoleChange = true }) => {
  const [catalog, setCatalog] = useState({ scopes: [], actions: [] });
  const [loading, setLoading] = useState(true);
  const [lastAppliedRole, setLastAppliedRole] = useState(null);

  // Load catalog once
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    axios.get(`${API_URL}/api/admin/permissions/document-scopes/catalog`)
      .then((r) => { if (!cancel) setCatalog(r.data || { scopes: [], actions: [] }); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  // Auto-fill from preset when role changes
  useEffect(() => {
    if (!role || !autoFillOnRoleChange) return;
    if (role === lastAppliedRole) return;
    let cancel = false;
    axios.get(`${API_URL}/api/admin/permissions/preset/${role}`)
      .then((r) => {
        if (cancel) return;
        const preset = r.data?.document_scopes || {};
        onChange?.(preset);
        setLastAppliedRole(role);
      })
      .catch(() => {});
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, autoFillOnRoleChange]);

  const matrix = value || {};
  const toggle = (scopeKey, action) => {
    const next = { ...(matrix || {}) };
    next[scopeKey] = { ...(next[scopeKey] || {}) };
    next[scopeKey][action] = !next[scopeKey][action];
    onChange?.(next);
  };

  if (role === 'super_admin') {
    return (
      <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800">
        <Lock className="h-4 w-4 text-amber-600" />
        <AlertDescription>
          <strong>Super Admin</strong> always has full access to every document scope.
        </AlertDescription>
      </Alert>
    );
  }

  if (loading) {
    return <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>;
  }

  const totalGranted = catalog.scopes.reduce((acc, s) => {
    const block = matrix[s.key] || {};
    return acc + catalog.actions.filter((a) => block[a]).length;
  }, 0);
  const totalCells = catalog.scopes.length * catalog.actions.length;

  return (
    <div className="space-y-3" data-testid="document-scopes-editor">
      <div className="flex items-center justify-between rounded-md border bg-muted/30 p-3">
        <div className="flex items-center gap-2 text-sm">
          <FolderOpen className="h-4 w-4 text-primary" />
          <span className="font-medium">Document Scopes</span>
          <Badge variant="outline" data-testid="document-scopes-stats">{totalGranted} / {totalCells} granted</Badge>
        </div>
      </div>

      <Alert className="py-2">
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          Grant per-scope access. A user with <strong>View</strong> can see documents in that scope.
          <strong> Upload</strong> additionally lets them upload, edit, archive and delete documents in that scope.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {catalog.scopes.map((s) => {
          const block = matrix[s.key] || {};
          return (
            <div
              key={s.key}
              className={`rounded-md border p-3 ${SCOPE_TONE[s.key] || ''}`}
              data-testid={`scope-row-${s.key}`}
            >
              <div className="font-medium">{s.label}</div>
              <p className="text-[11px] text-muted-foreground mb-3 leading-snug">{s.description}</p>
              <div className="space-y-2">
                {catalog.actions.map((a) => (
                  <label
                    key={a}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                    data-testid={`scope-cell-${s.key}-${a}`}
                  >
                    <Checkbox
                      checked={!!block[a]}
                      onCheckedChange={() => toggle(s.key, a)}
                    />
                    <span className="capitalize">{a}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentScopesEditor;
